import React, { useEffect, useState, useRef } from 'react';
import { useMotionValueEvent, MotionValue } from 'framer-motion';
interface ChatExamplesProps {
  scrollYProgress: MotionValue<number>;
  scrollStart: number;
  scrollEnd: number;
}
const MESSAGES = [{
  name: 'Sarah',
  avatar: 'bg-emerald-500',
  initials: 'S',
  time: '9:12 AM',
  text: <>
        Hey{' '}
        <span className="text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded" data-id="element-16">
          @giga
        </span>
        , scan my call notes from this morning and update{' '}
        <span className="text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded" data-id="element-17">
          @Hubspot
        </span>
      </>
}, {
  name: 'Alex',
  avatar: 'bg-purple-500',
  initials: 'A',
  time: '10:34 AM',
  text: <>
        Hey{' '}
        <span className="text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded" data-id="element-18">
          @giga
        </span>{' '}
        can you open a PR to update the homepage on the website
      </>
}, {
  name: 'David',
  avatar: 'bg-amber-500',
  initials: 'D',
  time: '11:20 AM',
  text: <>
        Hey Giga, can you help me think through the naming of our new product?
      </>
}, {
  name: 'Elena',
  avatar: 'bg-rose-500',
  initials: 'E',
  time: '1:45 PM',
  text: <>Hey Giga. What decisions did the team make today?</>
}, {
  name: 'Marcus',
  avatar: 'bg-sky-500',
  initials: 'M',
  time: '3:10 PM',
  text: <>Hey Giga. How are sales doing this week?</>
}, {
  name: 'Priya',
  avatar: 'bg-violet-500',
  initials: 'P',
  time: '4:22 PM',
  text: <>Hey Giga, this customer reported a bug — can you look into it for me?</>
}, {
  name: 'Tom',
  avatar: 'bg-teal-500',
  initials: 'T',
  time: '4:45 PM',
  text: <>
        Hey{' '}
        <span className="text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded" data-id="element-19">
          @giga
        </span>{' '}
        can you draft a recap of today's standup and post it to{' '}
        <span className="text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded" data-id="element-20">
          #engineering
        </span>
      </>
}, {
  name: 'Nina',
  avatar: 'bg-pink-500',
  initials: 'N',
  time: '5:02 PM',
  text: <>
        Hey Giga, what did our biggest customer ask about in their last support
        ticket?
      </>
}, {
  name: 'James',
  avatar: 'bg-orange-500',
  initials: 'J',
  time: '5:15 PM',
  text: <>
        Hey{' '}
        <span className="text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded" data-id="element-21">
          @giga
        </span>{' '}
        pull the latest NPS scores and compare them to last quarter
      </>
}, {
  name: 'Lena',
  avatar: 'bg-cyan-500',
  initials: 'L',
  time: '5:30 PM',
  text: <>Hey Giga, schedule a follow-up with the Acme team for next Tuesday</>
}, {
  name: 'Raj',
  avatar: 'bg-indigo-500',
  initials: 'R',
  time: '5:48 PM',
  text: <>
        Hey{' '}
        <span className="text-blue-400 bg-blue-500/10 px-1 py-0.5 rounded" data-id="element-22">
          @giga
        </span>{' '}
        what's the status of the API migration? Summarize across all channels
      </>
}, {
  name: 'Sarah',
  avatar: 'bg-emerald-500',
  initials: 'S',
  time: '6:10 PM',
  text: <>
        Hey Giga, write up a brief for the new onboarding flow based on today's
        user interviews
      </>
}];
export function ChatExamples({
  scrollYProgress,
  scrollStart,
  scrollEnd
}: ChatExamplesProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (latest <= scrollStart) {
      container.scrollTop = 0;
      return;
    }
    if (latest >= scrollEnd) {
      container.scrollTop = container.scrollHeight - container.clientHeight;
      return;
    }
    const progress = (latest - scrollStart) / (scrollEnd - scrollStart);
    const maxScroll = container.scrollHeight - container.clientHeight;
    container.scrollTop = progress * maxScroll;
  });
  return <div className="w-full max-w-3xl mx-auto relative" data-id="element-23">
      {/* Top fade */}
      <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-[#0a0a0a] to-transparent z-10 pointer-events-none rounded-t-2xl" data-id="element-24" />

      {/* Scroll-linked container */}
      <div ref={scrollContainerRef} className="h-[500px] overflow-hidden rounded-2xl px-4 flex flex-col gap-3" style={{
      paddingTop: '48px',
      paddingBottom: '48px'
    }} data-id="element-25">
        {MESSAGES.map((msg, idx) => <div key={`${msg.name}-${msg.time}-${idx}`} className="flex items-start gap-4 p-4 rounded-xl bg-neutral-900/40 border border-neutral-800/50 backdrop-blur-sm shadow-lg w-full shrink-0" data-id="element-26">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${msg.avatar} bg-opacity-20 border border-white/10`} data-id="element-27">
              <span className="text-sm font-medium text-white" data-id="element-28">
                {msg.initials}
              </span>
            </div>
            <div className="flex flex-col gap-1" data-id="element-29">
              <div className="flex items-baseline gap-2" data-id="element-30">
                <span className="font-medium text-white" data-id="element-31">{msg.name}</span>
                <span className="text-xs text-neutral-500" data-id="element-32">{msg.time}</span>
              </div>
              <p className="text-neutral-300 leading-relaxed" data-id="element-33">{msg.text}</p>
            </div>
          </div>)}
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#0a0a0a] to-transparent z-10 pointer-events-none rounded-b-2xl" data-id="element-34" />
    </div>;
}